import React, {useEffect} from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from '@mui/icons-material/Delete';
import {CircularProgress} from "@mui/material";
import {Link} from "react-router-dom";
import {DataGrid, GridToolbar} from "@mui/x-data-grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Button from "@mui/material/Button";
import {useTheme} from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AlertTitle from "@mui/material/AlertTitle";
import Alert from "@mui/material/Alert";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import ListItemButton from "@mui/material/ListItemButton";
import PostAddIcon from "@mui/icons-material/PostAdd";
import Divider from "@mui/material/Divider";
import {useTranslation} from "react-i18next";
import {UserContext} from "../App";
import {deleteData, fetchData} from "../utils/api";
import {makeStringReadable} from "../utils/table-utils";
import {reactMUILocale} from "../i18n";


export default function Overview() {
    const theme = useTheme();
    // init internationalization
    const {t} = useTranslation();

    const {datasets} = React.useContext(UserContext);
    const {fetchDatasets} = React.useContext(UserContext);
    const {setCurrentDatasetIdx} = React.useContext(UserContext);
    const {setCurrentExperimentIdx} = React.useContext(UserContext);

    const [experimentData, setExperimentData] = React.useState([]);
    const [columnInfo, setColumnInfo] = React.useState([]);
    const [selectedIndex, setSelectedIndex] = React.useState(0)
    const [selectedExperiments, setSelectedExperiments] = React.useState([])
    const [deleteType, setDeleteType] = React.useState([])
    const [open, setOpen] = React.useState(false);
    const [error, setError] = React.useState(false);
    const [deleteClicked, setDeleteClicked] = React.useState(false);
    const fetchExperiments = (datasetName) => {
        fetchData(
            `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/experiments`
        ).then((dataset) => {
            // unwrap dictionaries e.g. metrics and params and change key-val order
            const experimentsList = [];
            // create a new list of dictionaries, containing all experiment-relevant information
            // check if there are experiments for the current dataset
            if (dataset.experiments.length !== 0) {
                dataset.experiments.forEach((experiment) => {
                    const updatedExperiment = {
                        ...experiment,
                        is_created: experiment.experimentProgress.created,
                        is_processed: experiment.experimentProgress.processed,
                        num_trained_models: experiment.experimentProgress.trained,
                    };
                    delete updatedExperiment.experimentProgress;
                    experimentsList.push(updatedExperiment);
                });

                setExperimentData(experimentsList);
                // get column names, using the first entry
                const columnNames = Object.keys(experimentsList.at(0));
                // create column setting dictionary (needed for React MUI Data Grids)
                const columnInfoList = [];
                columnNames.reduce((obj, name) => {
                    columnInfoList.push({
                        // set readable header name
                        headerName: makeStringReadable(name),
                        field: name,
                        width: 150,
                    });
                    return obj;
                }, {});
                setColumnInfo(columnInfoList);
            } else {
                setExperimentData([]);
            }
        });
    };

    // set selectedIndex to clicked Dataset and display current Experiments
    const handleListItemClick = (event, index) => {
        setSelectedIndex(index);
        fetchExperiments(datasets[index].datasetName);
    };

    // Functions for the Dialog
    const handleDialogDisplay = () => {
        setOpen(!open);
    };


    // decide if Dataset or Experiment shall be deleted, needed for Dialog
    const handleDeleteType = (type) => {
        handleDialogDisplay()
        setDeleteType(type)
    }

    // delete selected Dataset
    const handleDatasetDelete = async (datasetName) => {
        try {
            await deleteData(
                `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}`
            ).then((data) => {
                if (data.status === 200) {
                    fetchDatasets({})
                    setSelectedIndex((prev) => Math.max(prev - 1, 0));
                    setDeleteClicked(false)
                } else {
                    setError(true)
                }
            })
        } catch (e) {
            console.log(e)
        }
    }
    // delete all selected Experiments
    const handleExperimentsDelete = async (datasetName, experimentNames) => {
        try {
            const deletePromises = experimentNames.map((experimentName) =>
                deleteData(
                    `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/experiment/${experimentName}`
                )
            );
            await Promise.all(deletePromises).then((responses) => {
                // Check if all deletions were successful (status 200)
                const allDeletionsSuccessful = responses.every((response) => response.status === 200);
                if (allDeletionsSuccessful) {
                    fetchExperiments(datasetName);
                } else {
                    setError(true)
                }
            });
        } catch (e) {
            console.log("error");
        }
    };

    // call Delete Function depending on type
    const handleDelete = () => {
        handleDialogDisplay()

        if (deleteType === "Experiment") {
            const experimentNames = selectedExperiments.map((idx) => experimentData[idx].experimentName)
            handleExperimentsDelete(datasets[selectedIndex].datasetName, experimentNames)
        } else if (deleteType === "Dataset") {
            handleDatasetDelete(datasets[selectedIndex].datasetName)
        }
    }
    const handleButtonClick = () => {
        // simple method to set variable to true after button click to display Spinner
        setDeleteClicked(true);
        // then call actual next handling method
        handleDelete();
    }
    // fetch experiments data by API requests, when the current dataset is changed
    useEffect(() => {
        if (datasets.length >= 1) {
            fetchExperiments(datasets[selectedIndex].datasetName);
        } else {
            setExperimentData([])
        }
    }, [datasets]);

    // set current selected Dataset and Experiment in sidebar to selected Dataset in Overview List
    useEffect(() => {
        setCurrentDatasetIdx(selectedIndex)
        setCurrentExperimentIdx(0)
    }, [selectedIndex]);

    return (
        <Grid
            id="overview-container"
            container
            columnSpacing={4}
            rowSpacing={2}
            sx={{padding: theme.spacing(8, 2)}}
        >
            <Grid item xs={4}>
                <Typography variant="h4" gutterBottom>
                    {" "}
                    {t("overview.MyDatasetsTitle")}
                    <Link to="/new-dataset" id="add-dataset-topbar-link">
                        <Tooltip title={t("overview.DataButton")}>
                            <IconButton aria-label={t("overview.DataButton")}>
                                <PostAddIcon/>
                            </IconButton>
                        </Tooltip>
                    </Link>
                </Typography>
            </Grid>
            <Grid item lg={8} xs={12}>
                <Typography variant="h4" gutterBottom>
                    {" "}
                    {t("overview.MyExperimentsTitle")}
                    <Link to="/new-experiment" id="add-experiment-topbar-link">
                        <Tooltip title={t("overview.ExperimentButton")}>
                            <IconButton aria-label={t("overview.ExperimentButton")}>
                                <AddCircleIcon/>
                            </IconButton>
                        </Tooltip>
                    </Link>
                    {selectedExperiments.length > 0 ?
                        <Tooltip title={t("overview.DeleteExperimentButton")}>
                            <IconButton onClick={() => handleDeleteType("Experiment")}>
                                <DeleteIcon/>
                            </IconButton>
                        </Tooltip>
                        : ""}

                </Typography>
            </Grid>
            <Grid item lg={4} xs={12}>
                <>
                    <List sx={{maxHeight: 600, overflow: 'auto'}}>
                        {datasets
                            ? datasets.map((ds, index) => (
                                <>
                                    <ListItem key={`ds-${ds.datasetName}`} selected={selectedIndex === index}
                                              onClick={(e) => handleListItemClick(e, index)} sx={{p: "0px"}}>
                                        <ListItemButton sx={{p: "8px"}}>
                                            <Grid container justifyContent="space-between" alignItems="center">
                                                <Grid item sx={{maxWidth: "80%"}}>
                                                    {index + 1}. {ds.datasetName}
                                                </Grid>
                                                <Grid item>
                                                    {!deleteClicked ? (
                                                        <Tooltip title={t("overview.DeleteDatasetButton")}>
                                                            <IconButton sx={{color: "inherit"}}
                                                                        onClick={() => handleDeleteType("Dataset")
                                                                        }>
                                                                <DeleteIcon/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : (
                                                        <CircularProgress/>
                                                    )}
                                                </Grid>
                                            </Grid>
                                        </ListItemButton>
                                    </ListItem>
                                    <Divider
                                        key={`divider-${ds.datasetName}`}
                                        variant="inset"
                                        component="li"
                                    />
                                </>
                            ))
                            : ""}
                    </List>
                    {/* Dialog to check if the selection shall be deleted */}
                    <Dialog
                        open={open}
                        onClose={handleDialogDisplay}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogContent>
                            <Alert severity="warning" className="abortUploadWarning"
                                   icon={<WarningAmberIcon
                                       sx={{
                                           alignItems: "center",
                                           width: "48px",
                                           height: "48px",
                                           color: theme.palette.error.main
                                       }}/>}>
                                <AlertTitle><Typography variant="h4"
                                                        color="text.secondary">{t("common.Warning")}</Typography>
                                </AlertTitle>
                                {deleteType === "Dataset" ? <Typography variant="h5"
                                                                        color="text.tertiary">{t("overview.DeleteDatasetWarning")}</Typography> :
                                    <Typography variant="h5"
                                                color="text.tertiary">{t("overview.DeleteExperimentWarning")}</Typography>
                                }
                            </Alert>
                        </DialogContent>
                        <DialogActions>
                            <Button variant="contained" color="primary" component="span"
                                    size="medium"
                                    onClick={handleButtonClick}
                                    autoFocus>
                                {t("common.Agree")}
                            </Button>
                            <Button variant="contained" color="primary" component="span"
                                    size="medium" onClick={handleDialogDisplay}> {t("common.Disagree")}</Button>

                        </DialogActions>
                    </Dialog>
                </>
            </Grid>
            <Grid item xs={8}>
                <DataGrid
                    rows={experimentData}
                    columns={columnInfo}
                    pageSize={9}
                    rowsPerPageOptions={[5]}
                    localeText={reactMUILocale}
                    getRowId={(row) => experimentData.indexOf(row)}
                    components={{
                        Toolbar: GridToolbar,
                    }}
                    onSelectionModelChange={selected => setSelectedExperiments(selected)}
                    autoHeight
                    checkboxSelection
                    disableRowSelectionOnClick
                />

            </Grid>
            {error &&
                <Grid item xs={4}>
                    <Alert severity="error" sx={{marginTop: "20px"}} variant="filled">
                        {t("experimentErrors.FailedDeletion")}
                    </Alert>
                </Grid>
            }
        </Grid>
    );
}
